import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import SearchSelect from '../components/ui/SearchSelect';
import { Plus, Search, Trash2, Edit2, FlaskConical, X, ArrowRight, Percent, Clock, ChefHat, ListOrdered, Eye, Info, Building2, FileDown, Link2, ClipboardList } from 'lucide-react';
import { toKg } from '../utils/unitConversion';

// Simple Modal Component
const Modal = ({ open, onClose, title, children, large }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-background rounded-lg shadow-xl mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto ${large ? 'w-full max-w-4xl' : 'w-full max-w-lg'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-chivo font-extrabold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ProcessedSupplies = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz, branchName } = useBranch();
  const [processedSupplies, setProcessedSupplies] = useState([]);
  const [basicSupplies, setBasicSupplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // For detail panel
  const [usageModalOpen, setUsageModalOpen] = useState(false); // For products using elaborado
  const [usageData, setUsageData] = useState({ elaborado: null, products: [] });
  const [usageLoading, setUsageLoading] = useState(false);
  
  // Production Order state
  const [productionOrderOpen, setProductionOrderOpen] = useState(false);
  const [productionItems, setProductionItems] = useState([]); // [{processed_id, name, quantity, priority}]
  const [productionTurno, setProductionTurno] = useState('Mañana');
  const [productionArea, setProductionArea] = useState('Cocina');
  const [productionPdfMode, setProductionPdfMode] = useState('blank'); // 'blank' = solo nombres con líneas en blanco
  
  const [form, setForm] = useState({
    name: '',
    ingredients: [], // Multiple ingredients: [{supply_id, quantity, unit}]
    output_quantity: '',
    output_unit: 'kg',
    notes: '',
    // Elaboration details
    prep_time_minutes: '',
    cook_time_minutes: '',
    presentation: '',
    difficulty_level: '',
    yield_portions: '',
    preparation_steps: []
  });
  
  // Temp ingredient input
  const [tempIngredient, setTempIngredient] = useState({
    supply_id: '',
    quantity: '',
    unit: 'kg'
  });

  useEffect(() => {
    fetchData();
  }, [currentBranch]); // Reload when branch changes

  const fetchData = async () => {
    try {
      // Build branch filter params
      const branchParams = currentBranch 
        ? `?branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const [processedRes, suppliesRes] = await Promise.all([
        api.get(`/processed-supplies${branchParams}`),
        api.get(`/supplies${branchParams}`)
      ]);
      setProcessedSupplies(processedRes.data);
      setBasicSupplies(suppliesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Fetch products that use a specific elaborado
  const fetchProductsUsingElaborado = async (elaborado) => {
    setUsageLoading(true);
    setUsageData({ elaborado, products: [] });
    setUsageModalOpen(true);
    
    try {
      const res = await api.get(`/products/using-elaborado/${elaborado.processed_id}`);
      setUsageData({ elaborado, products: res.data });
    } catch (error) {
      toast.error('Error al cargar productos');
      setUsageData({ elaborado, products: [] });
    } finally {
      setUsageLoading(false);
    }
  };

  // Production Order Functions
  const addToProductionOrder = (item) => {
    const existing = productionItems.find(p => p.processed_id === item.processed_id);
    if (existing) {
      toast.error('Este elaborado ya está en la orden');
      return;
    }
    setProductionItems([...productionItems, {
      processed_id: item.processed_id,
      name: item.name,
      quantity: item.output_quantity || 1,
      unit: item.output_unit || 'kg',
      priority: 'normal',
      ingredients: item.ingredients || [],
      output_quantity: item.output_quantity || 1,
      cost_per_unit: item.cost_per_unit || 0
    }]);
    toast.success(`${item.name} agregado a la orden`);
  };

  const removeFromProductionOrder = (processed_id) => {
    setProductionItems(productionItems.filter(p => p.processed_id !== processed_id));
  };

  const updateProductionItem = (processed_id, field, value) => {
    setProductionItems(productionItems.map(p => 
      p.processed_id === processed_id ? { ...p, [field]: value } : p
    ));
  };

  const calculateTotalIngredients = () => {
    const totals = {};
    productionItems.forEach(item => {
      const multiplier = parseFloat(item.quantity) || 0;
      (item.ingredients || []).forEach(ing => {
        const key = ing.supply_id || ing.name;
        const qtyInKg = ing.unit === 'gr' || ing.unit === 'g' 
          ? (parseFloat(ing.quantity) || 0) / 1000 
          : (parseFloat(ing.quantity) || 0);
        
        if (!totals[key]) {
          totals[key] = {
            name: basicSupplies.find(s => s.supply_id === ing.supply_id)?.name || ing.supply_id || 'Desconocido',
            quantity: 0,
            unit: 'kg'
          };
        }
        totals[key].quantity += qtyInKg * multiplier;
      });
    });
    return Object.values(totals).sort((a, b) => b.quantity - a.quantity);
  };

  const generateProductionOrderPdf = () => {
    if (productionItems.length === 0) {
      toast.error('Agrega al menos un elaborado a la orden');
      return;
    }

    const orderNumber = String(Date.now()).slice(-6);
    const today = new Date().toLocaleDateString('es-MX', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });

    // Helper function to get ingredient name (básico o elaborado)
    const getIngredientName = (supplyId) => {
      const basicSupply = basicSupplies.find(s => s.supply_id === supplyId);
      if (basicSupply) return basicSupply.name;
      
      const elaboradoSupply = processedSupplies.find(p => p.processed_id === supplyId);
      if (elaboradoSupply) return `🍳 ${elaboradoSupply.name}`;
      
      return supplyId || 'Desconocido';
    };

    // Generate recipe cards based on mode
    const recipeCards = productionItems.map(item => {
      const elaborado = processedSupplies.find(p => p.processed_id === item.processed_id);
      if (!elaborado) return '';

      const outputUnit = elaborado.output_unit || 'kg';
      const multiplier = parseFloat(item.quantity) || 1; // Cantidad a producir
      const outputQty = (elaborado.output_quantity || 1) * multiplier;
      
      // Calcular cantidades de ingredientes con el multiplicador
      const ingredients = (elaborado.ingredients || []).map(ing => ({
        name: getIngredientName(ing.supply_id),
        quantity: ((ing.quantity || ing.quantity_required || 0) * multiplier).toFixed(2),
        unit: ing.unit || 'kg',
        is_consumable: ing.is_consumable
      }));

      // MODE: Con Cantidades - Muestra ingredientes con cantidades calculadas
      return `
        <div class="recipe-card">
          <div class="recipe-header">
            <span class="checkbox"></span>
            <span class="recipe-title">${item.name}</span>
            <span class="priority-badge priority-${item.priority}">${item.priority === 'alta' ? '🔴 ALTA' : item.priority === 'media' ? '🟡 MEDIA' : '🟢 NORMAL'}</span>
          </div>
          <div class="recipe-body">
            <div class="blank-header">
              <span>Producir: <strong style="font-size:14px; color:#16a34a;">${outputQty.toFixed(2)}</strong> ${outputUnit}</span>
            </div>
            <table class="blank-table">
              <thead>
                <tr>
                  <th style="width:50%">Ingrediente</th>
                  <th style="width:30%">Cantidad Necesaria</th>
                  <th style="width:20%">✓</th>
                </tr>
              </thead>
              <tbody>
                ${ingredients.map(ing => `
                  <tr${ing.is_consumable ? ' style="background:#f3e8ff;"' : ''}>
                    <td class="ing-name">${ing.name}${ing.is_consumable ? ' <span style="font-size:9px;color:#7c3aed;">(costeo)</span>' : ''}</td>
                    <td style="font-weight:bold; color:#ea580c; font-size:13px;">${ing.quantity} ${ing.unit}</td>
                    <td class="check-cell">☐</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          <div class="recipe-notes">
            <span>Notas: <span class="write-line-full"></span></span>
          </div>
          <div class="recipe-complete">
            <span>Producido real: <span class="write-line"></span> ${outputUnit}</span>
            <span>Hora: <span class="write-line" style="min-width:50px"></span></span>
            <span>Firma: <span class="write-line"></span></span>
          </div>
        </div>
      `;
    }).join('');

    // Create printable HTML - Modo Solo Nombres
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Orden de Producción #${orderNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 12px; }
          .header h1 { font-size: 16px; margin-bottom: 3px; }
          .header h2 { font-size: 12px; font-weight: normal; color: #666; }
          .header-subtitle { font-size: 10px; color: #888; margin-top: 4px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 12px; padding: 8px; background: #f5f5f5; border-radius: 4px; }
          .info-item { text-align: center; }
          .info-label { font-size: 9px; color: #666; }
          .info-value { font-weight: bold; font-size: 11px; }
          
          .recipe-card { border: 2px solid #333; margin-bottom: 15px; border-radius: 6px; overflow: hidden; page-break-inside: avoid; }
          .recipe-header { background: #333; color: white; padding: 10px 12px; display: flex; align-items: center; gap: 10px; }
          .recipe-title { font-weight: bold; font-size: 14px; flex: 1; text-transform: uppercase; }
          .checkbox { width: 16px; height: 16px; border: 2px solid white; display: inline-block; border-radius: 3px; }
          .priority-badge { font-size: 10px; padding: 3px 8px; border-radius: 4px; font-weight: bold; }
          .priority-alta { background: #fee2e2; color: #dc2626; }
          .priority-media { background: #fef3c7; color: #d97706; }
          .priority-normal { background: #dcfce7; color: #16a34a; }
          
          .recipe-body { padding: 12px; }
          .blank-header { background: #e8f4fd; padding: 8px 12px; border-radius: 4px; margin-bottom: 10px; font-size: 12px; font-weight: bold; }
          
          .blank-table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .blank-table th { background: #f3f4f6; padding: 8px 10px; text-align: left; border: 1px solid #d1d5db; font-weight: bold; }
          .blank-table td { padding: 10px; border: 1px solid #d1d5db; }
          .blank-table .ing-name { font-weight: 500; }
          .blank-table .check-cell { text-align: center; font-size: 14px; width: 40px; }
          
          .write-line { border-bottom: 1px solid #666; min-width: 70px; display: inline-block; height: 18px; }
          .write-line-sm { border-bottom: 1px solid #666; min-width: 50px; display: inline-block; height: 16px; }
          .write-line-lg { border-bottom: 1px solid #333; min-width: 80px; display: inline-block; height: 18px; font-weight: bold; }
          .write-line-full { border-bottom: 1px solid #999; display: inline-block; width: calc(100% - 50px); height: 16px; }
          
          .recipe-notes { padding: 8px 12px; background: #fffbeb; border-top: 1px dashed #d1d5db; font-size: 10px; }
          .recipe-complete { padding: 10px 12px; background: #f0fdf4; border-top: 1px solid #d1d5db; font-size: 11px; display: flex; justify-content: space-between; gap: 15px; }
          
          .signatures { display: flex; justify-content: space-between; margin-top: 20px; padding-top: 15px; border-top: 2px solid #333; }
          .signature-box { text-align: center; width: 30%; }
          .signature-line { border-bottom: 1px solid #333; height: 30px; margin-bottom: 5px; }
          .signature-label { font-size: 9px; }
          
          @media print { 
            body { padding: 10px; } 
            .recipe-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${branchName || 'RESTAURANTE'}</h1>
          <h2>ORDEN DE PRODUCCIÓN #${orderNumber}</h2>
          <div class="header-subtitle">📝 Llenar cantidades a mano</div>
        </div>
        
        <div class="info-row">
          <div class="info-item">
            <div class="info-label">FECHA</div>
            <div class="info-value">${today}</div>
          </div>
          <div class="info-item">
            <div class="info-label">TURNO</div>
            <div class="info-value">${productionTurno}</div>
          </div>
          <div class="info-item">
            <div class="info-label">ÁREA</div>
            <div class="info-value">${productionArea}</div>
          </div>
          <div class="info-item">
            <div class="info-label">ELABORADOS</div>
            <div class="info-value">${productionItems.length}</div>
          </div>
        </div>

        ${recipeCards}

        <div class="signatures">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label"><strong>Almacén</strong><br/>Entregó insumos</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label"><strong>Elaboró</strong><br/>Nombre y firma</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div class="signature-label"><strong>Supervisó</strong><br/>Nombre y firma</div>
          </div>
        </div>

        <div style="text-align:center; margin-top:12px; font-size:8px; color:#999;">
          Generado: ${new Date().toLocaleString('es-MX')} | Registrar producción en sistema al finalizar
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
    
    toast.success('Orden de producción generada');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.ingredients.length === 0) {
      toast.error('Agrega al menos un ingrediente');
      return;
    }
    
    if (!form.output_quantity || parseFloat(form.output_quantity) <= 0) {
      toast.error('Ingresa la cantidad que se obtiene');
      return;
    }
    
    try {
      const data = {
        name: form.name,
        ingredients: form.ingredients.map(ing => ({
          supply_id: ing.supply_id,
          quantity: parseFloat(ing.quantity),
          unit: ing.unit
        })),
        output_quantity: parseFloat(form.output_quantity),
        output_unit: form.output_unit,
        notes: form.notes || null,
        // Elaboration details
        prep_time_minutes: form.prep_time_minutes ? parseInt(form.prep_time_minutes) : null,
        cook_time_minutes: form.cook_time_minutes ? parseInt(form.cook_time_minutes) : null,
        presentation: form.presentation || null,
        difficulty_level: form.difficulty_level || null,
        yield_portions: form.yield_portions ? parseInt(form.yield_portions) : null,
        preparation_steps: form.preparation_steps.length > 0 ? form.preparation_steps : null,
        // Branch assignment - for new items assign current branch (not for Matriz)
        branch_id: editing ? editing.branch_id : (isMatriz ? null : currentBranch?.branch_id)
      };

      if (editing) {
        await api.put(`/processed-supplies/${editing.processed_id}`, data);
        toast.success('Insumo elaborado actualizado');
      } else {
        await api.post('/processed-supplies', data);
        toast.success('Insumo elaborado creado');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    
    // Build ingredients list from new or legacy format
    let ingredients = [];
    if (item.ingredients && item.ingredients.length > 0) {
      ingredients = item.ingredients.map(ing => {
        // Look up the supply to check is_consumable
        const supply = basicSupplies.find(s => s.supply_id === ing.supply_id);
        return {
          supply_id: ing.supply_id,
          supply_name: ing.supply_name,
          quantity: ing.quantity.toString(),
          unit: ing.unit,
          is_consumable: supply?.is_consumable || false
        };
      });
    } else if (item.base_supply_id) {
      // Legacy single ingredient
      const supply = basicSupplies.find(s => s.supply_id === item.base_supply_id);
      ingredients = [{
        supply_id: item.base_supply_id,
        supply_name: item.base_supply_name,
        quantity: item.input_quantity?.toString() || '1',
        unit: item.base_supply_unit || 'kg',
        is_consumable: supply?.is_consumable || false
      }];
    }
    
    setForm({
      name: item.name,
      ingredients: ingredients,
      output_quantity: item.output_quantity.toString(),
      output_unit: item.output_unit || item.unit || 'kg',
      notes: item.notes || '',
      // Elaboration details
      prep_time_minutes: item.prep_time_minutes?.toString() || '',
      cook_time_minutes: item.cook_time_minutes?.toString() || '',
      presentation: item.presentation || '',
      difficulty_level: item.difficulty_level || '',
      yield_portions: item.yield_portions?.toString() || '',
      preparation_steps: item.preparation_steps || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (processedId) => {
    if (!window.confirm('¿Eliminar este insumo elaborado?')) return;
    try {
      await api.delete(`/processed-supplies/${processedId}`);
      toast.success('Insumo elaborado eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDownloadPdf = async (item) => {
    try {
      toast.info('Generando ficha PDF...');
      const response = await api.get(`/catalog-reports/elaborado-pdf/${item.processed_id}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = item.name.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_').slice(0, 50);
      link.setAttribute('download', `elaborado_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ficha PDF descargada');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: '',
      ingredients: [],
      output_quantity: '',
      output_unit: 'kg',
      notes: '',
      prep_time_minutes: '',
      cook_time_minutes: '',
      presentation: '',
      difficulty_level: '',
      yield_portions: '',
      preparation_steps: []
    });
    setTempIngredient({ supply_id: '', quantity: '', unit: 'kg', is_elaborado: false });
  };

  const addIngredient = () => {
    if (!tempIngredient.supply_id || !tempIngredient.quantity) {
      toast.error('Selecciona un insumo y cantidad');
      return;
    }
    
    // Check if already added
    if (form.ingredients.find(i => i.supply_id === tempIngredient.supply_id)) {
      toast.error('Este insumo ya está agregado');
      return;
    }
    
    // Buscar en insumos básicos o elaborados
    const basicSupply = basicSupplies.find(s => s.supply_id === tempIngredient.supply_id);
    const elaboradoSupply = processedSupplies.find(p => p.processed_id === tempIngredient.supply_id);
    
    const supply = basicSupply || elaboradoSupply;
    const isElaborado = !!elaboradoSupply;
    
    setForm({
      ...form,
      ingredients: [...form.ingredients, {
        supply_id: tempIngredient.supply_id,
        supply_name: supply?.name || '',
        quantity: tempIngredient.quantity,
        unit: tempIngredient.unit || supply?.unit || supply?.output_unit || 'kg',
        is_consumable: supply?.is_consumable || false,
        is_elaborado: isElaborado,
        source_area: isElaborado ? 'cocina' : null // Los elaborados se descuentan de cocina
      }]
    });
    setTempIngredient({ supply_id: '', quantity: '', unit: 'kg', is_elaborado: false });
  };

  const removeIngredient = (supplyId) => {
    setForm({
      ...form,
      ingredients: form.ingredients.filter(i => i.supply_id !== supplyId)
    });
  };

  const getTotalInputQuantity = () => {
    return form.ingredients.reduce((sum, ing) => {
      return sum + toKg(ing.quantity, ing.unit);
    }, 0);
  };

  // Calculate cost for a single ingredient (básico o elaborado)
  const getIngredientCost = (ing) => {
    // Buscar en insumos básicos
    const basicSupply = basicSupplies.find(s => s.supply_id === ing.supply_id);
    // Buscar en elaborados
    const elaboradoSupply = processedSupplies.find(p => p.processed_id === ing.supply_id);
    
    const supply = basicSupply || elaboradoSupply;
    if (!supply) return 0;
    
    const qty = parseFloat(ing.quantity) || 0;
    const costPerUnit = supply.cost_per_unit || 0;
    const supplyUnit = supply.unit || supply.output_unit || 'kg';
    
    // Convert if units don't match
    if ((ing.unit === 'gr' || ing.unit === 'g') && supplyUnit === 'kg') {
      return (qty / 1000) * costPerUnit;
    }
    if ((ing.unit === 'ml') && supplyUnit === 'lt') {
      return (qty / 1000) * costPerUnit;
    }
    return qty * costPerUnit;
  };

  // Calculate total recipe cost
  const getTotalRecipeCost = () => {
    return form.ingredients.reduce((sum, ing) => sum + getIngredientCost(ing), 0);
  };

  // Add/remove preparation steps
  const addPreparationStep = () => {
    setForm(prev => ({
      ...prev,
      preparation_steps: [...prev.preparation_steps, {
        step_number: prev.preparation_steps.length + 1,
        description: '',
        duration_minutes: null
      }]
    }));
  };

  const updatePreparationStep = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const removePreparationStep = (index) => {
    setForm(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, step_number: i + 1 }))
    }));
  };

  const filteredItems = processedSupplies.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.base_supply_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.ingredients?.some(i => i.supply_name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold flex items-center gap-3" data-testid="processed-supplies-title">
                <FlaskConical className="w-8 h-8 text-accent" />
                Insumos Elaborados
              </h1>
              <p className="text-muted-foreground">
                Insumos procesados con conversión de peso (cocidos, pelados, etc.)
              </p>
            </div>
            
            <Button 
              className="bg-accent text-white hover:bg-accent/90"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              data-testid="add-processed-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Elaborado
            </Button>
            <Button 
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50"
              onClick={() => setProductionOrderOpen(true)}
              data-testid="production-order-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Orden de Producción
              {productionItems.length > 0 && (
                <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {productionItems.length}
                </span>
              )}
            </Button>
          </div>

          {/* Info Card */}
          <Card className="mb-6 border-accent/30 bg-accent/5">
            <CardContent className="p-4">
              <p className="text-sm">
                <strong>¿Qué son los insumos elaborados?</strong> Son insumos que cambian de nombre y peso después de un proceso. 
                Ejemplo: <span className="text-accent font-semibold">1 kg de Camarón crudo ($180)</span> → al cocerlo → 
                <span className="text-accent font-semibold">0.7 kg de Camarón cocido ($257.14/kg)</span>
              </p>
            </CardContent>
          </Card>

          {/* Search and Count */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar insumos elaborados..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filteredItems.length}</span> elaborados
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 border-b px-4 py-3 grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <div className="col-span-3">Elaborado</div>
              <div className="col-span-3">Ingredientes</div>
              <div className="col-span-2 text-center">Conversión</div>
              <div className="col-span-1 text-center">Rend.</div>
              <div className="col-span-1 text-right">Costo</div>
              <div className="col-span-2 text-center">Acciones</div>
            </div>
            
            {/* Table Body */}
            <div className="divide-y max-h-[calc(100vh-380px)] overflow-y-auto">
              {filteredItems.map(item => {
                const ingredientCount = item.ingredients?.length || 1;
                const ingredientNames = item.ingredients?.map(i => i.supply_name).join(', ') || item.base_supply_name;
                
                return (
                  <div 
                    key={item.processed_id}
                    className="px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-50 transition-colors"
                  >
                    {/* Name */}
                    <div className="col-span-3">
                      <span className="font-bold text-purple-600">{item.name}</span>
                      {item.notes && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{item.notes}</p>
                      )}
                    </div>
                    
                    {/* Ingredients */}
                    <div className="col-span-3">
                      {ingredientCount === 1 ? (
                        <span className="text-sm text-gray-700">{ingredientNames}</span>
                      ) : (
                        <div>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            {ingredientCount} ingredientes
                          </span>
                          <p className="text-xs text-gray-500 truncate mt-0.5" title={ingredientNames}>
                            {ingredientNames}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Conversion */}
                    <div className="col-span-2 text-center">
                      <div className="flex items-center justify-center gap-1 text-sm">
                        <span className="text-gray-600">{item.input_quantity?.toFixed(1)}</span>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                        <span className="font-medium">{item.output_quantity} {item.unit}</span>
                      </div>
                    </div>
                    
                    {/* Yield */}
                    <div className="col-span-1 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                        item.yield_percentage >= 80 ? 'bg-green-100 text-green-700' :
                        item.yield_percentage >= 60 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {item.yield_percentage}%
                      </span>
                    </div>
                    
                    {/* Cost */}
                    <div className="col-span-1 text-right">
                      <span className="font-bold text-purple-600">${item.cost_per_unit?.toFixed(2)}</span>
                      <span className="text-xs text-gray-500">/{item.unit}</span>
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-center gap-1">
                      <button
                        className="p-1.5 hover:bg-orange-100 rounded-lg transition-colors"
                        onClick={() => addToProductionOrder(item)}
                        title="Agregar a Orden de Producción"
                      >
                        <ClipboardList className="w-4 h-4 text-orange-500" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-green-100 rounded-lg transition-colors"
                        onClick={() => fetchProductsUsingElaborado(item)}
                        title="Ver productos que lo usan"
                      >
                        <Link2 className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                        onClick={() => handleDownloadPdf(item)}
                        title="Descargar PDF"
                      >
                        <FileDown className="w-4 h-4 text-blue-500" />
                      </button>
                      {(item.prep_time_minutes || item.preparation_steps?.length > 0 || ingredientCount > 1) && (
                        <button
                          className="p-1.5 hover:bg-purple-100 rounded-lg transition-colors"
                          onClick={() => setSelectedItem(item)}
                          title="Ver detalles"
                        >
                          <Eye className="w-4 h-4 text-purple-500" />
                        </button>
                      )}
                      <button
                        className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                        onClick={() => handleEdit(item)}
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                        onClick={() => handleDelete(item.processed_id)}
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Empty State */}
            {filteredItems.length === 0 && !loading && (
              <div className="py-12 text-center">
                <FlaskConical className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No hay insumos elaborados registrados</p>
                <p className="text-sm text-gray-400 mt-2">Crea uno para calcular costos de ingredientes procesados</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal for Create/Edit */}
      <Modal
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        title={editing ? 'Editar Insumo Elaborado' : 'Nuevo Insumo Elaborado'}
        large
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <FlaskConical className="w-4 h-4" />
                Información Básica
              </h3>
              
              <div className="space-y-2">
                <Label>Nombre del Elaborado *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Salsa Bandera, Camarón Cocido..."
                  required
                  data-testid="processed-name"
                />
              </div>

              {/* Multiple Ingredients Section */}
              <div className="space-y-2">
                <Label>Ingredientes *</Label>
                <div className="border rounded-lg p-3 space-y-3">
                  {/* Add ingredient form */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchSelect
                        options={[
                          // Insumos básicos
                          ...basicSupplies.map(s => ({
                            ...s,
                            type: 'basic',
                            displayName: `${s.name} - $${s.cost_per_unit}/${s.unit}`
                          })),
                          // Insumos elaborados (para recetas que usan otros elaborados)
                          ...processedSupplies
                            .filter(p => !editing || p.processed_id !== editing.processed_id) // Excluir el mismo elaborado que estamos editando
                            .map(p => ({
                              supply_id: p.processed_id,
                              name: p.name,
                              cost_per_unit: p.cost_per_unit || 0,
                              unit: p.output_unit || 'kg',
                              type: 'elaborado',
                              is_elaborado: true,
                              displayName: `🍳 ${p.name} (Elaborado) - $${p.cost_per_unit?.toFixed(2) || '0.00'}/${p.output_unit || 'kg'}`
                            }))
                        ]}
                        value={tempIngredient.supply_id}
                        onChange={(val) => {
                          const selected = [...basicSupplies, ...processedSupplies.map(p => ({ supply_id: p.processed_id, unit: p.output_unit || 'kg', is_elaborado: true }))].find(s => s.supply_id === val || s.processed_id === val);
                          setTempIngredient({ 
                            ...tempIngredient, 
                            supply_id: val,
                            unit: selected?.unit || selected?.output_unit || 'kg',
                            is_elaborado: selected?.is_elaborado || false
                          });
                        }}
                        placeholder="Seleccionar insumo o elaborado..."
                        displayKey="displayName"
                        valueKey="supply_id"
                        renderOption={(opt) => (
                          <div className="flex justify-between items-center w-full">
                            <span className="font-medium flex items-center gap-1">
                              {opt.is_elaborado && <span className="text-orange-500">🍳</span>}
                              {opt.name}
                              {opt.is_consumable && (
                                <span className="bg-purple-100 text-purple-600 text-[9px] px-1 rounded">$</span>
                              )}
                              {opt.is_elaborado && (
                                <span className="bg-orange-100 text-orange-600 text-[9px] px-1 rounded">Elaborado</span>
                              )}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              ${opt.cost_per_unit}/{opt.unit}
                            </span>
                          </div>
                        )}
                      />
                    </div>
                    <Input
                      type="number"
                      step="0.001"
                      className="w-20"
                      value={tempIngredient.quantity}
                      onChange={(e) => setTempIngredient({ ...tempIngredient, quantity: e.target.value })}
                      placeholder="Cant."
                    />
                    <select
                      className="w-16 border rounded px-2 text-sm"
                      value={tempIngredient.unit}
                      onChange={(e) => setTempIngredient({ ...tempIngredient, unit: e.target.value })}
                    >
                      <option value="kg">kg</option>
                      <option value="gr">gr</option>
                      <option value="lt">lt</option>
                      <option value="ml">ml</option>
                      <option value="pz">pz</option>
                    </select>
                    <Button type="button" size="sm" onClick={addIngredient} data-testid="add-ingredient">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Ingredients list */}
                  {form.ingredients.length > 0 ? (
                    <div className="space-y-2">
                      {form.ingredients.map((ing, idx) => {
                        const ingCost = getIngredientCost(ing);
                        return (
                          <div key={ing.supply_id} className={`flex items-center justify-between p-2 rounded ${ing.is_consumable ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold ${ing.is_consumable ? 'bg-purple-200 text-purple-700' : 'bg-accent/20 text-accent'}`}>
                                {idx + 1}
                              </span>
                              <span className="font-medium">{ing.supply_name}</span>
                              {ing.is_consumable && (
                                <span className="bg-purple-200 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                  Solo costeo
                                </span>
                              )}
                              {ing.is_elaborado && (
                                <span className="bg-orange-200 text-orange-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                  🍳 Elaborado
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm">{parseFloat(ing.quantity || 0).toFixed(2)} {ing.unit}</span>
                              <span className="text-sm font-bold text-green-600">${ingCost.toFixed(2)}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                onClick={() => removeIngredient(ing.supply_id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex justify-between text-sm font-bold pt-2 border-t">
                        <span className="text-accent">Total entrada: {getTotalInputQuantity().toFixed(2)} kg</span>
                        <span className="text-green-600">Costo Total: ${getTotalRecipeCost().toFixed(2)}</span>
                      </div>
                      {/* Info about consumables */}
                      {form.ingredients.some(ing => ing.is_consumable) && (
                        <div className="text-xs text-purple-700 bg-purple-100 rounded p-2 flex items-start gap-1.5">
                          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>Los ingredientes "Solo costeo" se incluyen en el cálculo del costo pero NO se descuentan del inventario durante producción.</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Agrega los ingredientes que componen este elaborado
                    </p>
                  )}
                </div>
              </div>

              {/* Output */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cantidad que se Obtiene *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.output_quantity}
                    onChange={(e) => setForm({ ...form, output_quantity: e.target.value })}
                    placeholder="Ej: 1.5"
                    required
                    data-testid="output-quantity"
                  />
                  <p className="text-xs text-muted-foreground">Lo que resulta después</p>
                </div>
                <div className="space-y-2">
                  <Label>Unidad</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={form.output_unit}
                    onChange={(e) => setForm({ ...form, output_unit: e.target.value })}
                  >
                    <option value="kg">kg</option>
                    <option value="lt">lt</option>
                    <option value="pz">pz</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              {form.ingredients.length > 0 && form.output_quantity && (
                <div className="p-4 bg-accent/10 rounded-lg border border-accent/30">
                  <h4 className="font-bold text-sm mb-2">Vista Previa:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Entrada total:</span>
                      <span className="font-bold">{getTotalInputQuantity().toFixed(2)} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Rendimiento:</span>
                      <span className="font-bold">
                        {((parseFloat(form.output_quantity) / getTotalInputQuantity()) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between border-t pt-1 mt-1">
                      <span>Costo receta:</span>
                      <span className="font-bold text-green-600">${getTotalRecipeCost().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Costo por {form.output_unit}:</span>
                      <span className="font-bold text-orange-600">
                        ${(getTotalRecipeCost() / parseFloat(form.output_quantity)).toFixed(2)}/{form.output_unit}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Elaboration Details Section */}
            <div className="space-y-4">
              <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <ChefHat className="w-4 h-4" />
                Detalles de Elaboración (Opcional)
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Tiempo Preparación
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={form.prep_time_minutes}
                      onChange={(e) => setForm({ ...form, prep_time_minutes: e.target.value })}
                      placeholder="15"
                    />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Tiempo Cocción
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={form.cook_time_minutes}
                      onChange={(e) => setForm({ ...form, cook_time_minutes: e.target.value })}
                      placeholder="30"
                    />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Dificultad</Label>
                  <select
                    value={form.difficulty_level}
                    onChange={(e) => setForm({ ...form, difficulty_level: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="facil">Fácil</option>
                    <option value="medio">Medio</option>
                    <option value="dificil">Difícil</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Porciones que Rinde</Label>
                  <Input
                    type="number"
                    value={form.yield_portions}
                    onChange={(e) => setForm({ ...form, yield_portions: e.target.value })}
                    placeholder="4"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Presentación</Label>
                <Input
                  value={form.presentation}
                  onChange={(e) => setForm({ ...form, presentation: e.target.value })}
                  placeholder="Ej: Servir en plato hondo con decoración..."
                />
              </div>

              <div className="space-y-2">
                <Label>Notas Generales</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ej: Cocido en agua con sal..."
                />
              </div>
            </div>
          </div>

          {/* Preparation Steps Section */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <ListOrdered className="w-4 h-4" />
                Procedimiento ({form.preparation_steps.length} pasos)
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPreparationStep}
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar Paso
              </Button>
            </div>

            {form.preparation_steps.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4 bg-gray-50 rounded-lg">
                No hay pasos de preparación. Haz clic en "Agregar Paso" para comenzar.
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.preparation_steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <span className="w-6 h-6 rounded-full bg-accent text-white text-xs flex items-center justify-center flex-shrink-0 mt-1">
                      {step.step_number}
                    </span>
                    <div className="flex-1 space-y-2">
                      <Input
                        value={step.description}
                        onChange={(e) => updatePreparationStep(idx, 'description', e.target.value)}
                        placeholder="Describe este paso..."
                        className="text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={step.duration_minutes || ''}
                          onChange={(e) => updatePreparationStep(idx, 'duration_minutes', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Duración"
                          className="w-24 text-sm"
                        />
                        <span className="text-xs text-muted-foreground">min</span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive flex-shrink-0"
                      onClick={() => removePreparationStep(idx)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90">
            {editing ? 'Actualizar' : 'Crear'} Insumo Elaborado
          </Button>
        </form>
      </Modal>

      {/* Detail Panel Modal */}
      <Modal
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={`Procedimiento: ${selectedItem?.name || ''}`}
        large
      >
        {selectedItem && (
          <div className="space-y-6">
            {/* Times */}
            <div className="grid grid-cols-3 gap-4">
              {selectedItem.prep_time_minutes && (
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                  <p className="text-2xl font-bold text-blue-600">{selectedItem.prep_time_minutes}</p>
                  <p className="text-xs text-blue-600">min preparación</p>
                </div>
              )}
              {selectedItem.cook_time_minutes && (
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <ChefHat className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                  <p className="text-2xl font-bold text-orange-600">{selectedItem.cook_time_minutes}</p>
                  <p className="text-xs text-orange-600">min cocción</p>
                </div>
              )}
              {selectedItem.difficulty_level && (
                <div className={`rounded-lg p-4 text-center ${
                  selectedItem.difficulty_level === 'facil' ? 'bg-green-50' :
                  selectedItem.difficulty_level === 'medio' ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <p className={`text-lg font-bold capitalize ${
                    selectedItem.difficulty_level === 'facil' ? 'text-green-600' :
                    selectedItem.difficulty_level === 'medio' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {selectedItem.difficulty_level}
                  </p>
                  <p className="text-xs text-muted-foreground">Dificultad</p>
                </div>
              )}
            </div>

            {/* Presentation */}
            {selectedItem.presentation && (
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-bold text-sm text-purple-700 mb-2">Presentación</h4>
                <p className="text-purple-800">{selectedItem.presentation}</p>
              </div>
            )}

            {/* Steps */}
            {selectedItem.preparation_steps?.length > 0 && (
              <div>
                <h4 className="font-bold text-sm text-muted-foreground uppercase tracking-wide mb-3">
                  Procedimiento Paso a Paso
                </h4>
                <div className="space-y-3">
                  {selectedItem.preparation_steps.map((step, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <span className="w-8 h-8 rounded-full bg-accent text-white font-bold flex items-center justify-center flex-shrink-0">
                        {step.step_number}
                      </span>
                      <div className="flex-1">
                        <p>{step.description}</p>
                        {step.duration_minutes && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {step.duration_minutes} minutos
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedItem.notes && (
              <div className="bg-gray-100 rounded-lg p-4">
                <h4 className="font-bold text-sm mb-2">Notas</h4>
                <p className="text-sm">{selectedItem.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal: Products Using this Elaborado */}
      <Modal
        open={usageModalOpen}
        onClose={() => setUsageModalOpen(false)}
        title={usageData.elaborado?.name || 'Uso en Productos'}
        large
      >
        <div className="space-y-4">
          {/* Elaborado Info Header */}
          {usageData.elaborado && (
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div>
                <div className="text-lg font-bold text-green-800">{usageData.elaborado.name}</div>
                <div className="text-sm text-green-600">
                  Costo: ${(usageData.elaborado.cost_per_unit || 0).toFixed(2)} / {usageData.elaborado.output_unit || 'kg'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-700">{usageData.products.length}</div>
                <div className="text-xs text-green-600">productos lo usan</div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {usageLoading && (
            <div className="py-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-500">Buscando productos...</p>
            </div>
          )}

          {/* Products List */}
          {!usageLoading && usageData.products.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 p-3 bg-gray-100 font-medium text-sm text-gray-700">
                <div className="col-span-5">Producto</div>
                <div className="col-span-2 text-center">Categoría</div>
                <div className="col-span-2 text-center">Cantidad</div>
                <div className="col-span-2 text-center">Costo</div>
                <div className="col-span-1 text-center">Acción</div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {usageData.products.map((product, idx) => (
                  <div 
                    key={product.product_id} 
                    className={`grid grid-cols-12 gap-2 p-3 items-center text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50 transition-colors`}
                  >
                    <div className="col-span-5 font-medium truncate" title={product.name}>
                      {product.name}
                    </div>
                    <div className="col-span-2 text-center text-gray-500 text-xs">
                      {product.category || '-'}
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="font-bold text-green-700">{product.quantity_used}</span>
                      <span className="text-gray-500 ml-1">{product.unit}</span>
                    </div>
                    <div className="col-span-2 text-center text-orange-600 font-medium">
                      ${(product.cost_in_recipe || 0).toFixed(2)}
                    </div>
                    <div className="col-span-1 text-center">
                      <button
                        onClick={() => {
                          setUsageModalOpen(false);
                          window.location.href = `/products?edit=${product.product_id}`;
                        }}
                        className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                        title="Editar producto"
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Summary */}
              <div className="p-3 bg-green-50 border-t flex justify-between items-center">
                <span className="text-sm text-green-700">
                  Total consumo por venta de estos productos:
                </span>
                <span className="font-bold text-green-800">
                  {usageData.products.reduce((sum, p) => sum + (p.quantity_used || 0), 0).toFixed(2)} {usageData.elaborado?.output_unit || 'kg'}
                </span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!usageLoading && usageData.products.length === 0 && (
            <div className="py-8 text-center">
              <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">Ningún producto usa este elaborado</p>
              <p className="text-sm text-gray-400 mt-1">Agrega este elaborado a las recetas de tus productos</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal: Production Order */}
      <Modal
        open={productionOrderOpen}
        onClose={() => setProductionOrderOpen(false)}
        title="Orden de Producción"
        large
      >
        <div className="space-y-4">
          {/* Settings Row */}
          <div className="grid grid-cols-3 gap-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div>
              <Label className="text-xs text-gray-600">Turno</Label>
              <select
                value={productionTurno}
                onChange={(e) => setProductionTurno(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              >
                <option value="Mañana">Mañana</option>
                <option value="Tarde">Tarde</option>
                <option value="Noche">Noche</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Área</Label>
              <select
                value={productionArea}
                onChange={(e) => setProductionArea(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              >
                <option value="Cocina">Cocina</option>
                <option value="Cocina Fría">Cocina Fría</option>
                <option value="Cocina Caliente">Cocina Caliente</option>
                <option value="Preparación">Preparación</option>
                <option value="Barra">Barra</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-center w-full p-2 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-orange-600">{productionItems.length}</div>
                <div className="text-xs text-gray-500">Items en orden</div>
              </div>
            </div>
          </div>

          {/* Production Items List */}
          {productionItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No hay items en la orden</p>
              <p className="text-sm">Haz clic en el ícono 📋 de cada elaborado para agregarlo</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 p-3 bg-gray-100 font-medium text-xs text-gray-700">
                <div className="col-span-4">Elaborado</div>
                <div className="col-span-2 text-center">Cantidad</div>
                <div className="col-span-2 text-center">Unidad</div>
                <div className="col-span-3 text-center">Prioridad</div>
                <div className="col-span-1"></div>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {productionItems.map((item, idx) => (
                  <div 
                    key={item.processed_id} 
                    className={`grid grid-cols-12 gap-2 p-3 items-center text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                  >
                    <div className="col-span-4 font-medium truncate">{item.name}</div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={item.quantity}
                        onChange={(e) => updateProductionItem(item.processed_id, 'quantity', e.target.value)}
                        className="text-center text-sm h-8"
                      />
                    </div>
                    <div className="col-span-2 text-center text-gray-500">{item.unit}</div>
                    <div className="col-span-3">
                      <select
                        value={item.priority}
                        onChange={(e) => updateProductionItem(item.processed_id, 'priority', e.target.value)}
                        className={`w-full px-2 py-1 border rounded text-xs font-medium ${
                          item.priority === 'alta' ? 'bg-red-100 text-red-700 border-red-300' :
                          item.priority === 'media' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                          'bg-green-100 text-green-700 border-green-300'
                        }`}
                      >
                        <option value="alta">🔴 Alta</option>
                        <option value="media">🟡 Media</option>
                        <option value="normal">🟢 Normal</option>
                      </select>
                    </div>
                    <div className="col-span-1 text-center">
                      <button
                        onClick={() => removeFromProductionOrder(item.processed_id)}
                        className="p-1 hover:bg-red-100 rounded text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total Ingredients Preview */}
          {productionItems.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="p-3 bg-blue-50 font-medium text-sm text-blue-800 border-b">
                📦 Insumos necesarios (calculado automáticamente)
              </div>
              <div className="max-h-32 overflow-y-auto p-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {calculateTotalIngredients().map((ing, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                      <span>{ing.name}</span>
                      <span className="font-bold">{ing.quantity.toFixed(2)} {ing.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setProductionItems([]);
                toast.success('Orden limpiada');
              }}
              disabled={productionItems.length === 0}
              className="flex-1"
            >
              Limpiar Orden
            </Button>
            <Button
              onClick={generateProductionOrderPdf}
              disabled={productionItems.length === 0}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Generar PDF
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProcessedSupplies;
